import { DateTime } from 'luxon'

import type { VolunteerActivityType } from '@/queries/constants'
import { LINK_REQUIRED_ACTIVITY_TYPES } from '@/client/services/volunteerActivity'

import type { FormInputType, Options } from '@/components/UI/Form/types'
import { VOLUNTEER_ACTIVITIES_METADATA } from '@/components/VolunteerHours/constants'
import type { VolunteerActivityComponentAttributes } from '@/components/VolunteerHours/types'

export const VOLUNTEER_ACTIVITY_FORM_TYPE_NAME = 'type'
export const VOLUNTEER_ACTIVITY_FORM_SESSION_PREP_ID = 'session_prep_session_id'

export default function getSubmitVolunteerActivityFormInputs({
  existingActivity,
  selectedType,
  sessionPrepSessionOptions,
  volunteerTeamOptions,
  nonVolunteerTeamOptions,
}: {
  existingActivity?: VolunteerActivityComponentAttributes | null
  selectedType?: VolunteerActivityType | null
  sessionPrepSessionOptions: Options
  volunteerTeamOptions: Options
  nonVolunteerTeamOptions: Options
}): FormInputType[] {
  const {
    type: defaultType,
    date: defaultDate,
    duration_seconds: defaultDurationSeconds,
    description: defaultDescription,
    links: defaultLinks,
    related_entity: defaultRelatedEntity,
  } = existingActivity ?? {}

  const metadata = selectedType != null ? VOLUNTEER_ACTIVITIES_METADATA[selectedType] : null

  const formInputs: FormInputType[] = [
    {
      name: VOLUNTEER_ACTIVITY_FORM_TYPE_NAME,
      initialValue: defaultType ?? selectedType,
      type: 'select-group',
      label: 'Activity type',
      options: [
        {
          label: 'Non-Volunteer Team Activities',
          options: nonVolunteerTeamOptions,
        },
        ...(volunteerTeamOptions.length > 0
          ? [
              {
                label: 'Volunteer Team Activities',
                options: volunteerTeamOptions,
              },
            ]
          : []),
      ],
      required: true,
      size: 4,
      sizeSpan: 4,
      // Do not allow modifying activity type after creation
      disabled: existingActivity != null,
    },
    {
      name: 'date',
      initialValue: defaultDate?.toISOString() ?? DateTime.local().toISO(),
      type: 'date',
      label: 'Date',
      required: true,
      size: 2,
    },
    {
      name: VOLUNTEER_ACTIVITY_FORM_SESSION_PREP_ID,
      initialValue:
        defaultRelatedEntity?.type === 'host-session-prep' ? `${defaultRelatedEntity.id}` : null,
      type: 'select',
      label: 'Relevant Session',
      sublabel: `You may only select sessions that aren't already associated with another activity`,
      options: sessionPrepSessionOptions,
      required: true,
      size: 6,
      dependencies: {
        keys: [VOLUNTEER_ACTIVITY_FORM_TYPE_NAME],
        callback(values) {
          return (
            (values[VOLUNTEER_ACTIVITY_FORM_TYPE_NAME] as VolunteerActivityType) ===
            'host-session-prep'
          )
        },
      },
    },
    {
      name: 'duration_minutes',
      initialValue:
        defaultDurationSeconds != null ? Math.ceil(defaultDurationSeconds / 60) : undefined,
      type: 'number',
      label: 'Duration (minutes)',
      max: metadata != null ? metadata.maximumMinutes : undefined,
      sublabel:
        metadata != null
          ? `You should not submit a duration over ${metadata.maximumMinutes} minutes as nothing above that will be accepted`
          : null,
      placeholder: '30',
      required: true,
      size: 6,
    },
    {
      name: 'description',
      initialValue: defaultDescription != null ? `${defaultDescription}` : null,
      type: 'textarea',
      label: 'Description',
      sublabel: 'Please be as detailed as possible',
      placeholder: metadata != null ? metadata.defaultDescription : null,
      required: true,
      size: 6,
    },
    {
      name: 'links',
      initialValue: defaultLinks != null ? `${defaultLinks}` : null,
      type: 'text',
      label: 'Relevant Links',
      sublabel: 'Ensure links are publicly accessible. Separate multiple links with commas.',
      placeholder: 'schoolhouse.world/session/X, schoolhouse.world/user/Y, ...',
      required: selectedType != null && LINK_REQUIRED_ACTIVITY_TYPES.has(selectedType),
      size: 6,
    },
    {
      name: 'session_confirmation',
      type: 'multi-checkbox',
      label: 'Confirmation',
      options: [
        {
          label:
            'I have attached evidence of my session preparation based on the guidelines listed above and ensured that the link is publicly available.',
          value: 'confirm',
        },
      ],
      required: true,
      size: 6,
      dependencies: {
        keys: [VOLUNTEER_ACTIVITY_FORM_TYPE_NAME],
        callback(values) {
          return (
            (values[VOLUNTEER_ACTIVITY_FORM_TYPE_NAME] as VolunteerActivityType) ===
            'host-session-prep'
          )
        },
      },
    },
    {
      name: 'permission_confirmation',
      type: 'multi-checkbox',
      label: 'Confirmation',
      options: [
        {
          label:
            'I have permission to submit a request under this category from a Schoolhouse product team member. I have listed their name in the description box.',
          value: 'confirm',
        },
      ],
      required: true,
      size: 6,
      dependencies: {
        keys: [VOLUNTEER_ACTIVITY_FORM_TYPE_NAME],
        callback(values) {
          return (values[VOLUNTEER_ACTIVITY_FORM_TYPE_NAME] as VolunteerActivityType) === 'other'
        },
      },
    },
  ]

  return formInputs
}
